import {
  Checkbox,
  ClickAwayListener,
  createStyles,
  Fade,
  MenuItem,
  MenuList,
  Popper,
  TablePagination,
  TableSortLabel,
  Tooltip,
  Typography,
  WithStyles
} from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import { Theme, withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { ArrowDropDown } from '@material-ui/icons';
import classnames from 'classnames';
import React from 'react';
import { IFilter } from '../../../models/states';
import {
  getSorting,
  LightenDarkenColor,
  rowIndicators,
  stableSort
} from '../../../utilities/utilities';

/**************************************** Styles **********************************************************************/

const styles = (theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      marginTop: theme.spacing.unit * 3,
      overflowX: 'auto'
    },
    table: {
      minWidth: 700
    },
    blackBackground: {
      // background: theme.palette.background.default
    },
    pinkBackground: {
      backgroundColor: `${theme.tertiaryColors.pink} !important`,
      '&:hover': {
        backgroundColor: `${LightenDarkenColor(
          theme.tertiaryColors.pink,
          40
        )} !important`
      }
    },
    redBackground: {
      backgroundColor: `${theme.tertiaryColors.red} !important`,
      '&:hover': {
        backgroundColor: `${LightenDarkenColor(
          theme.tertiaryColors.red,
          40
        )} !important`
      }
    },
    orangeBackground: {
      backgroundColor: `${theme.tertiaryColors.orange} !important`,
      '&:hover': {
        backgroundColor: `${LightenDarkenColor(
          theme.tertiaryColors.orange,
          40
        )} !important`
      }
    },
    header: {
      backgroundColor: theme.tertiaryColors.grey,
      textTransform: 'uppercase',
      height: '2.5rem',
      whiteSpace: 'nowrap'
    },
    filterHeader: {
      display: 'flex',
      alignItems: 'center',
      cursor: 'pointer'
    },
    filterLabel: {
      zIndex: 1300
    },
    tableRow: {
      textTransform: 'uppercase',
      ...theme.tableRow
    },
    tableCell: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: '30ch',
      paddingLeft: '1rem'
    },
    tableHead: {
      paddingRight: '0rem',
      paddingLeft: '1rem'
    },
    lightTooltip: {
      backgroundColor: theme.palette.common.white,
      color: 'rgba(0, 0, 0, 0.87)',
      boxShadow: theme.shadows[1],
      fontSize: 11
    },
    checkboxRoot: {
      '&$checked': theme.checkbox.yellow
    },
    checked: {},
    lowercase: {
      textTransform: 'none'
    },
    stickyHeader: {
      // position: 'sticky',
      // // top: '3rem',
      // background: 'grey'
    },
    blur: {
      filter: 'blur(2px)'
    }
  });

/********************************** Classes ***************************************************************************/
class MyTable extends React.Component<IMyTableProps, IMyTableState> {
  constructor(props: IMyTableProps) {
    super(props);
    this.state = {
      isOpenFilter: false,
      anchorEl: null,
      orderBy: '',
      order: 'asc',
      page: 0,
      rowsPerPage: props.rowsPerPage ? props.rowsPerPage : 25
    };
  }
  componentWillMount() {
    const { order, orderBy } = this.props;
    this.setState({
      order: order ? order : this.state.order,
      orderBy: orderBy ? orderBy : this.state.orderBy
    });
  }
  // createSortHandler = (
  //   event: React.MouseEvent<HTMLElement>,
  //   property: string
  // ) => {
  //   this.handleRequestSort(event, property);
  // };

  handleCloseFilter = (dataKey: string) => {
    this.setState({ isOpenFilter: false, anchorEl: null });
    this.props.onToggleHeaderFilters(dataKey);
  };

  handleOpenFilter = (e: React.MouseEvent<HTMLElement>, dataKey: string) => {
    this.setState({ anchorEl: e.target });
    this.props.onToggleHeaderFilters(dataKey);
  };

  handleChangePage = (event: React.MouseEvent<HTMLElement>, page: number) => {
    this.setState({ page });
  };
  handleChangeRowsPerPage = event => {
    this.setState({ rowsPerPage: parseInt(event.target.value, 10) });
  };
  handleRequestSort = (
    event: React.MouseEvent<HTMLElement>,
    property: string
  ) => {
    const orderBy = property;
    let order: 'asc' | 'desc' = 'desc';

    if (this.state.orderBy === property && this.state.order === 'desc') {
      order = 'asc';
    }

    this.setState({ orderBy, order });
  };

  renderSortHeader = (
    header: IMyTableHeader,
    order: 'asc' | 'desc',
    orderBy: string
  ): React.ReactNode => {
    return (
      <TableSortLabel
        active={orderBy === header.dataKey}
        direction={order}
        onClick={e => this.handleRequestSort(e, header.dataKey)}
      >
        {header.label}
      </TableSortLabel>
    );
  };

  renderFilterHeader = (
    header: IMyTableHeader,
    order: 'asc' | 'desc',
    orderBy: string
  ): React.ReactNode => {
    const { classes } = this.props;
    const filters: IFilter[] =
      header.filters !== undefined ? [...header.filters] : [];
    return (
      <div>
        <div
          onClick={e => this.handleOpenFilter(e, header.dataKey)}
          className={classes.filterHeader}
        >
          <Typography style={{ fontSize: '0.75rem' }}>
            {header.label}
          </Typography>

          <ArrowDropDown />
        </div>

        <Popper
          open={
            this.props.isOpenFilters[header.dataKey]
              ? this.props.isOpenFilters[header.dataKey]
              : false
          }
          anchorEl={this.state.anchorEl}
          className={classes.filterLabel}
          transition
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={100}>
              <ClickAwayListener
                onClickAway={() => this.handleCloseFilter(header.dataKey)}
              >
                <Paper style={{ overflowY: 'scroll', maxHeight: '40vh' }}>
                  <MenuList>
                    {filters.map(filter => {
                      return (
                        <MenuItem
                          key={filter.label}
                          style={{ textTransform: 'uppercase', paddingLeft: 0 }}
                          onClick={() =>
                            this.props.onToggleFilters(header.dataKey, filter)
                          }
                        >
                          <Checkbox
                            checked={filter.isSet}
                            value="checkedB"
                            color="primary"
                            classes={{
                              root: classes.checkboxRoot,
                              checked: classes.checked
                            }}
                          />
                          <Typography variant="caption">
                            {filter.label}
                          </Typography>
                        </MenuItem>
                      );
                    })}
                  </MenuList>
                </Paper>
              </ClickAwayListener>
            </Fade>
          )}
        </Popper>
      </div>
    );
  };

  renderTableHead = (
    tHead: IMyTableHeader[],
    orderBy: string,
    order: 'asc' | 'desc'
  ): React.ReactNode => {
    const { classes } = this.props;
    return (
      <TableHead>
        <TableRow className={classes.header}>
          {tHead.map((header, index) => {
            return (
              <TableCell
                key={header.dataKey + index}
                className={classnames(classes.tableHead, classes.stickyHeader)}
              >
                {header.isSort || header.isFilter
                  ? header.isSort
                    ? this.renderSortHeader(header, order, orderBy)
                    : this.renderFilterHeader({ ...header }, order, orderBy)
                  : header.label}
              </TableCell>
            );
          })}
        </TableRow>
      </TableHead>
    );
  };

  renderTablePagination = (
    tRows: IMyTableRows[],
    rowsPerPage: number,
    page: number
  ): React.ReactNode => {
    return (
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100, 150]}
        component="div"
        count={tRows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        backIconButtonProps={{
          'aria-label': 'Previous Page'
        }}
        nextIconButtonProps={{
          'aria-label': 'Next Page'
        }}
        onChangePage={this.handleChangePage}
        onChangeRowsPerPage={this.handleChangeRowsPerPage}
      />
    );
  };

  renderTableBody = (
    tRows: IMyTableRows[],
    tHead: IMyTableHeader[],
    page: number,
    rowsPerPage: number,
    order: string,
    orderBy: string,
    orderByWith: string
  ): React.ReactNode => {
    const { classes } = this.props;
    let rows = stableSort(tRows, getSorting(order, orderBy)).slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );

    if (orderByWith) {
      const tempRows = rows && rows.filter(row => row.ended_at === 'NA');
      const tempRowsSortedByEndedAt =
        rows && rows.filter(row => row.ended_at !== 'NA');
      let tempRowsSortedByStartedAt = [];
      if (tempRows.length > 1) {
        tempRowsSortedByStartedAt = stableSort(
          tempRows,
          getSorting(order, 'timestamp')
        );
        rows = [...tempRowsSortedByStartedAt, ...tempRowsSortedByEndedAt];
      }
    }

    return (
      <TableBody>
        {rows.map(
          (row: IMyTableRows, index: number): React.ReactNode => {
            return (
              <TableRow
                hover
                key={row.dataKey}
                className={classnames(classes.tableRow, {
                  [classes.blackBackground]: index % 2 !== 0,
                  [classes.pinkBackground]:
                    row.rowIndicator === rowIndicators.PINK,
                  [classes.redBackground]:
                    row.rowIndicator === rowIndicators.RED,
                  [classes.orangeBackground]:
                    row.rowIndicator === rowIndicators.YELLOW
                })}
              >
                {tHead.map((header, idx) => {
                  let tableCell: any;
                  const cellContent = rows[index][header.dataKey]
                    ? rows[index][header.dataKey]
                    : 'NA';

                  if (header.tooltip) {
                    tableCell = (
                      <Tooltip
                        placement="bottom-start"
                        classes={{ tooltip: classes.lightTooltip }}
                        title={
                          header.dataKey === 'state'
                            ? rows[index].stateErrorMessage
                            : rows[index][header.dataKey]
                        }
                        enterDelay={300}
                      >
                        <span>{cellContent}</span>
                      </Tooltip>
                    );
                  } else {
                    tableCell = cellContent;
                  }
                  return (
                    <TableCell
                      key={row[header.dataKey].dataKey + header.dataKey}
                      className={classnames(classes.tableCell, {
                        [classes.lowercase]: header.lowercase,
                        [classes.blur]: header.blur
                      })}
                    >
                      {tableCell}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          }
        )}
      </TableBody>
    );
  };

  public render() {
    const { classes, tRows, tHead, orderByWith } = this.props;
    const { page, rowsPerPage, order, orderBy } = this.state;
    return (
      <Paper className={classes.root}>
        <div>
          <Table className={classes.table}>
            {/* {this.props.colWidths ? (
              <colgroup>
                {this.props.colWidths.map((width, idx) => {
                  return <col width={width.toString() + "%"} key={idx} />;
                })}
              </colgroup>
            ) : null} */}
            {tHead.length !== 0
              ? this.renderTableHead(tHead, orderBy, order)
              : null}
            {tRows.length !== 0
              ? this.renderTableBody(
                  tRows,
                  tHead,
                  page,
                  rowsPerPage,
                  order,
                  orderBy,
                  orderByWith
                )
              : null}
          </Table>
          {this.renderTablePagination(tRows, rowsPerPage, page)}
        </div>
      </Paper>
    );
  }
}
/************************************ INTERFACES **********************************************************************/
interface IMyTableHeader {
  label: string;
  dataKey: string;
  isFilter: boolean; // flag to enable filters for the header
  align?: string; // column alignment
  filters?: IFilter[]; // array of filters on the the header
  numeric?: boolean; // flag to specify if the column fields are numeric
  isSort?: boolean; // for sorting on the column
  tooltip?: boolean;
  lowercase?: boolean;
  blur?: boolean;
}

interface IMyTableRows {
  dataKey: string | number;
  rowIndicator?: string;
  [key: string]: any;
}

interface IMyTableProps extends WithStyles<typeof styles> {
  tRows: IMyTableRows[];
  tHead: IMyTableHeader[];
  multiSelect: boolean;
  order: 'asc' | 'desc';
  orderBy: string;
  orderByWith?: string;
  page?: number;
  rowsPerPage?: number;
  onToggleFilters?: any; // todo: give proper signature
  onChangeRowsPerPage?: any;
  onChangePage?: any;
  isOpenFilters?: any;
  onToggleHeaderFilters?: any;
  colWidths?: number[];
}

interface IMyTableState {
  isOpenFilter: boolean;
  anchorEl: any;
  orderBy: string;
  order: 'asc' | 'desc';
  page: number;
  rowsPerPage: number;
}

export default withStyles(styles)(MyTable);
